import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, addDoc, serverTimestamp, query, where } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";
import { decryptPatient } from "../utils/encryption";

function QuickAssessmentPage() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [patientsLoaded, setPatientsLoaded] = useState(false);
  const [form, setForm] = useState({
    patientId: "",
    painLevel: "",
    rangeOfMotion: "",
    mobilityStatus: "",
    assessmentNotes: "",
    recommendations: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        setLoading(false);
        setPatientsLoaded(true);
        return;
      }

      const loadPatients = async () => {
        try {
          const snap = await getDocs(
            query(
              collection(db, "patients"),
              where("userId", "==", currentUser.uid)
            )
          );
          const data = snap.docs.map((d) =>
            decryptPatient({ id: d.id, ...d.data() }),
          );
          setPatients(data);
        } catch (e) {
          console.warn("Failed to load patients", e);
        } finally {
          setLoading(false);
          setPatientsLoaded(true);
        }
      };

      loadPatients();
    });

    return () => unsubscribe();
  }, []);

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.patientId) {
      setError("Please select a patient");
      return;
    }

    if (!form.painLevel || !form.mobilityStatus) {
      setError("Please fill in all required fields");
      return;
    }

    setSaving(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setError("You must be logged in");
        setSaving(false);
        return;
      }

      const selectedPatient = patients.find((p) => p.id === form.patientId);
      
      // Save assessment to a new collection
      await addDoc(collection(db, "assessments"), {
        patientId: form.patientId,
        patientName: selectedPatient?.name || "Unknown",
        painLevel: form.painLevel,
        rangeOfMotion: form.rangeOfMotion || "",
        mobilityStatus: form.mobilityStatus,
        assessmentNotes: form.assessmentNotes || "",
        recommendations: form.recommendations || "",
        userId: currentUser.uid,
        createdAt: serverTimestamp(),
      });

      // Navigate back to dashboard
      navigate("/doctor");
    } catch (e) {
      console.error(e);
      setError("Failed to save assessment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="screen">
      <header className="form-header">
        <button className="icon-btn" onClick={() => navigate("/doctor")}>
          ←
        </button>
        <h2>Quick Assessment</h2>
        <button
          type="submit"
          form="assessment-form"
          className="save-btn"
          disabled={saving || loading}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </header>

      {loading ? (
        <div className="form-card">
          <p className="muted">Loading patients...</p>
        </div>
      ) : patientsLoaded && patients.length === 0 ? (
        <div className="form-card">
          <div style={{ textAlign: "center", padding: "48px 24px" }}>
            <span 
              className="material-symbols-outlined" 
              style={{ 
                fontSize: "64px", 
                color: "var(--text-muted)", 
                marginBottom: "16px", 
                display: "block" 
              }}
            >
              person_add
            </span>
            <h3 style={{ margin: "0 0 8px", color: "var(--text-primary)", fontSize: "20px", fontWeight: "800" }}>
              No Patients Found
            </h3>
            <p style={{ color: "var(--text-muted)", fontSize: "15px", fontWeight: "500", margin: "0 0 24px", lineHeight: "1.6" }}>
              You need to add at least one patient before you can perform an assessment.
            </p>
            <button
              type="button"
              onClick={() => navigate("/patients/new")}
              style={{
                background: "var(--register-primary)",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "12px",
                fontSize: "15px",
                fontWeight: "600",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s ease"
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = "var(--register-primary-hover)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = "var(--register-primary)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: "20px" }}>
                person_add
              </span>
              Add Patient
            </button>
          </div>
        </div>
      ) : (
        <form id="assessment-form" className="form-card" onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}

          <div className="form-section">
            <label className="form-label">Patient *</label>
            <select
              className="form-select"
              value={form.patientId}
              onChange={handleChange("patientId")}
              required
            >
              <option value="">Select Patient</option>
              {patients.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-section">
            <label className="form-label">Pain Level *</label>
            <select
              className="form-select"
              value={form.painLevel}
              onChange={handleChange("painLevel")}
              required
            >
              <option value="">Select Pain Level</option>
              <option value="None">None (0)</option>
              <option value="Mild">Mild (1-3)</option>
              <option value="Moderate">Moderate (4-6)</option>
              <option value="Severe">Severe (7-9)</option>
              <option value="Extreme">Extreme (10)</option>
            </select>
          </div>

          <div className="form-section">
            <label className="form-label">Range of Motion</label>
            <select
              className="form-select"
              value={form.rangeOfMotion}
              onChange={handleChange("rangeOfMotion")}
            >
              <option value="">Select Range</option>
              <option value="Full">Full</option>
              <option value="Limited">Limited</option>
              <option value="Restricted">Restricted</option>
              <option value="Minimal">Minimal</option>
            </select>
          </div>

          <div className="form-section">
            <label className="form-label">Mobility Status *</label>
            <select
              className="form-select"
              value={form.mobilityStatus}
              onChange={handleChange("mobilityStatus")}
              required
            >
              <option value="">Select Status</option>
              <option value="Independent">Independent</option>
              <option value="Assisted">Assisted</option>
              <option value="Dependent">Dependent</option>
              <option value="Wheelchair">Wheelchair</option>
              <option value="Bedridden">Bedridden</option>
            </select>
          </div>

          <div className="form-section">
            <label className="form-label">Assessment Notes</label>
            <textarea
              className="form-textarea"
              rows="4"
              placeholder="Brief notes about the assessment..."
              value={form.assessmentNotes}
              onChange={handleChange("assessmentNotes")}
            />
          </div>

          <div className="form-section">
            <label className="form-label">Recommendations</label>
            <textarea
              className="form-textarea"
              rows="3"
              placeholder="Treatment recommendations or next steps..."
              value={form.recommendations}
              onChange={handleChange("recommendations")}
            />
          </div>

          <button type="submit" className="primary-btn-full" disabled={saving}>
            {saving ? "Saving Assessment..." : "Save Assessment"}
          </button>
        </form>
      )}
    </div>
  );
}

export default QuickAssessmentPage;
